<script lang="ts">
    import { ChatCircle as ChatCircleIcon } from 'phosphor-svelte';
    import { promptInput, isPrompting, selectedNodeIds, isChatOpen, space, interactionsUpdateTrigger, promptHistory } from '../../stores';
    import spinnerVideo from '../../assets/spinner.mp4';
    import logoIcon from '../../assets/logo.png';
    import { marked } from 'marked';

    export let onSubmit: (prompt: string, objectIds?: string[], effort?: string) => Promise<void>;

    let inputValue = '';

    // Visible chat bubbles floating above the bar
    type Bubble = { id: number; role: 'user' | 'assistant'; content: string; fading: boolean };
    let bubbles: Bubble[] = [];
    let bubbleId = 0;
    let lastSeenMessageCount = 0;
    const BUBBLE_DURATION = 8000;
    const FADE_DURATION = 400;

    marked.use({ breaks: true, gfm: true });

    function renderMarkdown(content: string): string {
        return marked.parse(content, { async: false }) as string;
    }

    function cleanUserInput(input: string): string {
        const userIndex = input.indexOf('[USER]');
        if (userIndex !== -1) {
            return input.slice(userIndex + '[USER]'.length).trim();
        }
        return input;
    }

    function parseActions(content: string): string {
        return content.replace(/```actions\n[\s\S]*?```/g, '').trim();
    }

    /** Schedule the fade-out timer for a bubble */
    function scheduleFade(id: number) {
        setTimeout(() => {
            bubbles = bubbles.map(b => b.id === id ? { ...b, fading: true } : b);
        }, BUBBLE_DURATION);

        setTimeout(() => {
            bubbles = bubbles.filter(b => b.id !== id);
        }, BUBBLE_DURATION + FADE_DURATION);
    }

    /** IDs of bubbles whose fade timer is deferred until prompting completes */
    let pendingFadeBubbles: number[] = [];

    function addBubble(role: 'user' | 'assistant', content: string) {
        const id = ++bubbleId;
        bubbles = [...bubbles, { id, role, content, fading: false }];

        if ($isPrompting) {
            // Defer fade — will be started when prompting finishes
            pendingFadeBubbles = [...pendingFadeBubbles, id];
        } else {
            scheduleFade(id);
        }
    }

    // When prompting finishes, start fade timers for all deferred bubbles
    $: if (!$isPrompting && pendingFadeBubbles.length > 0) {
        for (const id of pendingFadeBubbles) {
            scheduleFade(id);
        }
        pendingFadeBubbles = [];
    }

    // Watch for new messages
    $: {
        if ($space && $interactionsUpdateTrigger >= 0) {
            const interactions = $space.getInteractions();
            const promptInteractions = interactions.filter((i: any) => i.operation === 'prompt');

            // Count total messages (user + assistant)
            let totalMessages = 0;
            for (const interaction of promptInteractions) {
                totalMessages++; // user message
                if (interaction.output !== null) {
                    totalMessages++; // assistant response
                }
            }

            if (totalMessages > lastSeenMessageCount && lastSeenMessageCount > 0) {
                // Find new messages
                let count = 0;
                for (const interaction of promptInteractions) {
                    count++;
                    if (count > lastSeenMessageCount - (lastSeenMessageCount > 0 ? 0 : 0)) {
                        // Check if this user message is new
                        if (count > lastSeenMessageCount) {
                            addBubble('user', cleanUserInput(interaction.input));
                        }
                    }
                    if (interaction.output !== null) {
                        count++;
                        if (count > lastSeenMessageCount) {
                            addBubble('assistant', parseActions(interaction.output));
                        }
                    }
                }
            }

            lastSeenMessageCount = totalMessages;
        }
    }

    promptInput.subscribe(value => {
        inputValue = value;
    });

    $: selectedIds = Array.from($selectedNodeIds);

    function handleInput(e: Event) {
        const target = e.target as HTMLInputElement;
        promptInput.set(target.value);
        promptHistory.reset();
    }

    function handleKeydown(e: KeyboardEvent) {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSubmit(e);
        } else if (e.key === 'ArrowUp') {
            const prev = promptHistory.up(inputValue);
            if (prev !== null) {
                e.preventDefault();
                promptInput.set(prev);
            }
        } else if (e.key === 'ArrowDown') {
            const next = promptHistory.down();
            if (next !== null) {
                e.preventDefault();
                promptInput.set(next);
            }
        }
    }

    async function handleSubmit(e: Event) {
        e.preventDefault();
        if (!inputValue.trim() || $isPrompting) return;

        const prompt = inputValue.trim();
        const objectIds = selectedIds.length > 0 ? selectedIds : undefined;

        promptHistory.push(prompt);
        promptInput.set('');
        isPrompting.set(true);

        await onSubmit(prompt, objectIds);

        isPrompting.set(false);
    }

    function openMainChat() {
        isChatOpen.set(true);
    }
</script>

<div class="mini-chat-wrapper">
    <!-- Chat bubbles floating above -->
    {#if bubbles.length > 0}
        <div class="bubble-stack">
            {#each bubbles as bubble (bubble.id)}
                <div class="bubble {bubble.role}" class:fading={bubble.fading}>
                    {#if bubble.role === 'assistant'}
                        <img src={logoIcon} alt="" class="bubble-logo" />
                        <div class="bubble-content markdown-content">{@html renderMarkdown(bubble.content)}</div>
                    {:else}
                        <div class="bubble-content">{bubble.content}</div>
                    {/if}
                </div>
            {/each}
        </div>
    {/if}

    <!-- Input bar + glow wrapper -->
    <div class="mini-chat-container">
        <!-- Holographic glow border (fades in/out, sits behind the form) -->
        <div class="glow-border" class:active={$isPrompting}>
            <div class="glow-ring"></div>
            <div class="glow-blur"></div>
        </div>

        <form on:submit={handleSubmit} class="mini-chat" class:thinking={$isPrompting}>
        <button
            class="chat-button"
            type="button"
            aria-label="Open chat"
            title="Open chat"
            on:click={openMainChat}
        >
            <ChatCircleIcon size={18} />
        </button>

        <input
            type="text"
            value={inputValue}
            on:input={handleInput}
            placeholder={$isPrompting ? "Thinking..." : "Ask anything..."}
            disabled={$isPrompting}
            class="chat-input"
            on:keydown={handleKeydown}
        />

        <button
            type="submit"
            disabled={$isPrompting || !inputValue.trim()}
            class="send-button"
            aria-label="Send"
        >
            {#if $isPrompting}
                <video src={spinnerVideo} autoplay loop muted playsinline class="button-spinner"></video>
            {:else}
                <svg class="send-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 19V5M5 12l7-7 7 7"/>
                </svg>
            {/if}
        </button>
        </form>
    </div>
</div>

<style>
    .mini-chat-wrapper {
        position: absolute;
        bottom: 32px;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        z-index: 9998;
        pointer-events: none;
        width: 560px;
        max-width: calc(100vw - 80px);
    }

    /* Bubble stack */
    .bubble-stack {
        display: flex;
        flex-direction: column;
        gap: 6px;
        width: 100%;
        pointer-events: auto;
        overflow: visible;
        padding: 4px;
    }

    .bubble {
        position: relative;
        padding: 10px 14px;
        font-size: 0.9rem;
        line-height: 1.5;
        max-width: 100%;
        word-wrap: break-word;
        animation: bubbleIn 0.25s ease-out;
        opacity: 1;
        transition: opacity 0.4s ease-out;
        box-shadow:
            0px 4px 12px -2px rgba(0, 0, 0, 0.1),
            0px 2px 4px -1px rgba(0, 0, 0, 0.06);
    }

    .bubble-content {
        overflow-wrap: break-word;
        display: -webkit-box;
        -webkit-line-clamp: 4;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    .bubble.fading {
        opacity: 0;
    }

    /* User bubble: pinched bottom-right corner like iMessage */
    .bubble.user {
        align-self: flex-end;
        background: var(--panelSolid);
        border: 0.36px solid var(--border);
        border-radius: 16px 16px 4px 16px;
        color: var(--text);
        max-width: 80%;
        margin-bottom: 4px;
    }

    /* Assistant bubble: pinched bottom-left corner */
    .bubble.assistant {
        align-self: flex-start;
        background: var(--panelSolid);
        border: 0.36px solid var(--border);
        border-radius: 16px 16px 16px 4px;
        color: var(--text);
        max-width: 90%;
        display: flex;
        align-items: flex-start;
        gap: 8px;
        margin-bottom: 4px;
    }

    .bubble-logo {
        width: 18px;
        height: 18px;
        flex-shrink: 0;
        margin-top: 2px;
        opacity: 0.5;
    }

    @keyframes bubbleIn {
        from {
            opacity: 0;
            transform: translateY(8px) scale(0.97);
        }
        to {
            opacity: 1;
            transform: translateY(0) scale(1);
        }
    }

    /* Markdown in assistant bubbles */
    .bubble.assistant .markdown-content :global(p) {
        margin: 0 0 0.4em 0;
    }

    .bubble.assistant .markdown-content :global(p:last-child) {
        margin-bottom: 0;
    }

    .bubble.assistant .markdown-content :global(ul),
    .bubble.assistant .markdown-content :global(ol) {
        margin: 0.3em 0;
        padding-left: 1.3em;
    }

    .bubble.assistant .markdown-content :global(li) {
        margin: 0.15em 0;
    }

    .bubble.assistant .markdown-content :global(code) {
        background: var(--assistantBubble);
        padding: 0.1em 0.3em;
        border-radius: 3px;
        font-size: 0.85em;
    }

    .bubble.assistant .markdown-content :global(a) {
        color: #2E90FA;
        text-decoration: underline;
    }

    /* Input bar */
    .mini-chat {
        position: relative;
        z-index: 1;
        display: flex;
        align-items: center;
        gap: 12px;
        width: 100%;
        padding: 10px 14px;
        pointer-events: auto;

        background: var(--panelSolid);
        border: 0.36px solid var(--border);
        border-radius: 32px;
        box-shadow:
            0px 5.23px 6.54px -3.92px rgba(0, 0, 0, 0.1),
            0px 13.08px 16.35px -3.27px rgba(0, 0, 0, 0.1);

        transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }

    .mini-chat:focus-within {
        border-color: var(--accent);
        box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.15);
    }

    .mini-chat.thinking {
        border-color: transparent;
    }

    /* Holographic glow — container stacks glow behind the form */
    .mini-chat-container {
        position: relative;
        width: 100%;
        pointer-events: none;
    }

    .glow-border {
        position: absolute;
        inset: 0;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.4s ease;
        z-index: 0;
    }

    .glow-border.active {
        opacity: 1;
    }

    .glow-ring {
        position: absolute;
        inset: -2px;
        border-radius: 34px;
        padding: 2px;
        background: conic-gradient(
            from var(--glow-angle, 0deg),
            #a855f7,
            #6366f1,
            #06b6d4,
            #22d3ee,
            #a855f7,
            #ec4899,
            #a855f7
        );
        -webkit-mask:
            linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask:
            linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
        mask-composite: exclude;
        animation: glow-spin 3s linear infinite;
    }

    .glow-blur {
        position: absolute;
        inset: -4px;
        border-radius: 36px;
        background: conic-gradient(
            from var(--glow-angle, 0deg),
            #a855f7,
            #6366f1,
            #06b6d4,
            #22d3ee,
            #a855f7,
            #ec4899,
            #a855f7
        );
        filter: blur(12px);
        opacity: 0.4;
        animation: glow-spin 3s linear infinite;
    }

    @keyframes glow-spin {
        to {
            --glow-angle: 360deg;
        }
    }

    @property --glow-angle {
        syntax: '<angle>';
        initial-value: 0deg;
        inherits: false;
    }

    /* Chat bubble button */
    .chat-button {
        position: relative;
        width: 38px;
        height: 38px;
        display: grid;
        place-items: center;
        flex-shrink: 0;

        background: transparent;
        border: 0.36px solid var(--border);
        border-radius: 50%;
        color: var(--muted);
        cursor: pointer;

        transition: background 0.15s ease, color 0.15s ease;
    }

    .chat-button:hover {
        background: var(--assistantBubble);
        color: var(--text);
    }

    .chat-button:active {
        transform: scale(0.95);
    }



    /* Text input */
    .chat-input {
        flex: 1;
        min-width: 0;
        padding: 8px 0;
        border: none;
        font-size: 1rem;
        font-family: inherit;
        outline: none;
        color: var(--text);
        background: transparent;
        line-height: 1.6;
    }

    .chat-input::placeholder {
        color: var(--faint);
    }

    .chat-input:disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }

    /* Send button */
    .send-button {
        padding: 0;
        background: var(--panelSolid);
        color: var(--text);
        border: 0.36px solid var(--border);
        border-radius: 50%;
        cursor: pointer;
        transition: background 0.15s ease, transform 0.06s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 38px;
        height: 38px;
        flex-shrink: 0;
    }

    .send-icon {
        width: 20px;
        height: 20px;
    }

    .button-spinner {
        width: 26px;
        height: 26px;
        object-fit: contain;
    }

    .send-button:hover:not(:disabled) {
        background: var(--bg0);
    }

    .send-button:active:not(:disabled) {
        transform: scale(0.95);
    }

    .send-button:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }
</style>
